"use client";

import { createAccountLocalisedSchema, EditPersonalInfoSchema } from "@/lib/validation/account";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Button } from "@/components/atoms/buttons";
import RadioButton from "@/components/atoms/radio-buttons";
import { Locales } from "@/lib/i18n";
import { sdk } from "@/lib/medusa/client";
import { useEffect, useState } from "react";
import { __, getPermalink } from "@/lib/i18n/functions";
import { useCustomer } from "@/hooks/useCustomer";
import { useRouter } from "next/navigation";

type EditPersonalInfoFormProps = {
  locale: Locales;
};

const EditPersonalInfoForm = ({ locale }: EditPersonalInfoFormProps) => {
  const router = useRouter();
  const { customer, setCustomer } = useCustomer();
  const { editPersonalInfoSchema } = createAccountLocalisedSchema(locale);
  const [loading, setLoading] = useState<boolean>(false);

  const form = useForm<EditPersonalInfoSchema>({
    resolver: zodResolver(editPersonalInfoSchema),
    defaultValues: {
      title: undefined,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      birthDate: "",
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = form;

  useEffect(() => {
    if (customer) {
      reset({
        title: customer.metadata?.title as "mademoiselle" | "madame" | "monsieur" | undefined,
        firstName: customer.first_name || "",
        lastName: customer.last_name || "",
        email: customer.email || "",
        phone: customer.phone || "",
        birthDate: (customer.metadata?.birth_date as string | undefined) || "",
      });
    }
  }, [customer, reset]);

  const onSubmit = async (data: EditPersonalInfoSchema) => {
    setLoading(true);
    try {
      const response = await sdk.store.customer.update({
        first_name: data.firstName,
        last_name: data.lastName,
        phone: data.phone,
        metadata: {
          ...(customer?.metadata || {}),
          title: data.title,
          birth_date: data.birthDate || "",
        },
      });

      setCustomer(response.customer);
      router.push(getPermalink(locale, "account"));
    } catch (error) {
      console.error("Failed to update customer:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="grid gap-x-5 gap-y-2.5 sm:grid-cols-2">
          <FormField
            control={control}
            name="title"
            render={({ field }) => (
              <FormItem className="w-full gap-1 sm:col-span-2">
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">{__("Civility", locale)}</FormLabel>
                  <RadioButton
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    options={[
                      { value: "mademoiselle", label: __("Miss ", locale).trim() },
                      { value: "madame", label: __("Mrs ", locale).trim() },
                      { value: "monsieur", label: __("Mr", locale).trim() },
                    ]}
                  />
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="firstName"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.lastName && !errors.firstName && "mb-6")}>
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">{__("First name", locale)}</FormLabel>
                  <FormControl>
                    <Input
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] bg-white placeholder:text-sm focus-visible:ring-[2px] sm:max-w-[358px]"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="lastName"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.firstName && !errors.lastName && "mb-6")}>
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">{__("Last name", locale)}</FormLabel>
                  <FormControl>
                    <Input
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px] sm:max-w-[358px]"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.phone && !errors.email && "mb-6")}>
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">{__("Email", locale)}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px] sm:max-w-[358px]"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="phone"
            render={({ field }) => (
              <FormItem className={cn("w-full gap-1", errors.firstName && !errors.phone && "mb-6")}>
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">{__("Telephone", locale)}</FormLabel>
                  <FormControl>
                    <Input
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px] sm:max-w-[358px]"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="birthDate"
            render={({ field }) => (
              <FormItem className="w-full gap-1">
                <div className="flex flex-col gap-2.5">
                  <FormLabel className="mb-0 text-base font-medium">{__("Date of birth", locale)}</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm focus-visible:ring-[2px] sm:max-w-[358px]"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <Button size="md" className="w-full text-base font-semibold sm:w-fit" type="submit">
          {__(`${loading ? "Loading..." : "Save changes"}`, locale)}
        </Button>
      </form>
    </Form>
  );
};

export default EditPersonalInfoForm;
